import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import {
    Calendar,
    ChevronLeft,
    ChevronRight,
    Clock,
    Eye,
    Filter,
    MapPin,
    Search,
    Tag,
    TrendingUp,
    User,
} from 'lucide-react';

interface BlogPostSummary {
    slug: string;
    title: string;
    excerpt: string;
    category: string;
    categoryName: string;
    categoryColor: string;
    tags: string[];
    author: string;
    date: string;
    featured: boolean;
    readingTime: number;
    views: number;
    coverImage?: string;
}

interface BlogCategory {
    slug: string;
    name: string;
    description: string;
    color: string;
    postCount: number;
    views: number;
}

interface BlogTag {
    name: string;
    slug: string;
    count: number;
    views: number;
}

interface BlogStats {
    totalPosts: number;
    publishedPosts: number;
    categories: number;
    tags: number;
    totalViews: number;
    viewsToday: number;
}

interface BlogProfile {
    name: string;
    title: string;
    avatar?: string;
    bio: string;
    location?: string;
    social?: Record<string, string>;
}

const BlogList: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [posts, setPosts] = useState<BlogPostSummary[]>([]);
    const [categories, setCategories] = useState<BlogCategory[]>([]);
    const [tags, setTags] = useState<BlogTag[]>([]);
    const [profile, setProfile] = useState<BlogProfile | null>(null);
    const [stats, setStats] = useState<BlogStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 9;

    useEffect(() => {
        const category = searchParams.get('category');
        if (category) {
            setSelectedCategory(category);
        }
        
        const q = searchParams.get('q');
        if (q) {
            setSearchQuery(q);
        }
    }, [searchParams]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [overviewRes, postsRes, tagsRes] = await Promise.all([
                    fetch('/api/blog-admin/overview'),
                    fetch('/api/blog-admin/posts?page=1&pageSize=200'),
                    fetch('/api/blog-admin/tags'),
                ]);

                const overviewData = await overviewRes.json();
                const postsData = await postsRes.json();
                const tagsData = await tagsRes.json();

                if (overviewData.success) {
                    setProfile(overviewData.data.profile || null);
                    setCategories(overviewData.data.categories || []);
                    setStats(overviewData.data.stats || null);
                }

                if (postsData.success) {
                    setPosts(postsData.data || []);
                }

                if (tagsData.success) {
                    setTags(tagsData.data || []);
                }
            } catch (error) {
                console.error('获取博客数据失败:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const filteredPosts = useMemo(() => {
        return posts.filter((post) => {
            const query = searchQuery.toLowerCase();
            const matchesSearch = !query
                || post.title.toLowerCase().includes(query)
                || post.excerpt.toLowerCase().includes(query)
                || post.tags.some((tag) => tag.toLowerCase().includes(query));
            const matchesCategory = !selectedCategory || post.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [posts, searchQuery, selectedCategory]);

    const totalPages = Math.max(1, Math.ceil(filteredPosts.length / postsPerPage));
    const pageStart = (currentPage - 1) * postsPerPage;
    const paginatedPosts = filteredPosts.slice(pageStart, pageStart + postsPerPage);

    const activeCategoryName = selectedCategory
        ? (categories.find((category) => category.slug === selectedCategory)?.name || '未分类')
        : '全部文章';

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const handleCategoryChange = (categorySlug: string) => {
        setSelectedCategory(categorySlug);
        setCurrentPage(1);

        if (!categorySlug) {
            setSearchParams({});
            return;
        }

        setSearchParams({ category: categorySlug });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">加载中...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 sm:py-12 px-3 sm:px-4 md:px-6 lg:px-8">
            <SEO 
                title="博客文章"
                description="沉淀技术，记录生活。涵盖软件开发项目经验、生活随笔杂记和阅读思考片段。"
            />
            <div className="w-full max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-6 sm:mb-8 md:mb-12"
                >
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 dark:text-white mb-3 sm:mb-4">
                        文章记录
                    </h1>
                    <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300">
                        记录工作、学习、生活与任何值得写下的内容
                    </p>
                </motion.div>

                <div className="grid gap-4 sm:gap-6 lg:gap-8 lg:grid-cols-[1fr] xl:grid-cols-[minmax(0,1fr)_minmax(0,320px)] min-w-0">
                    <div className="min-w-0">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-4 sm:mb-6 md:mb-8"
                        >
                            <div className="relative">
                                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="搜索文章、标签或关键字..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 dark:text-white text-sm sm:text-base"
                                />
                            </div>
                        </motion.div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-4 sm:mb-6">
                            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">{activeCategoryName}</h2>
                            <span className="text-sm text-gray-500 dark:text-gray-400">共 {filteredPosts.length} 篇</span>
                        </div>

                        <div className="space-y-4 sm:space-y-6">
                            {paginatedPosts.map((post, index) => (
                                <motion.div
                                    key={post.slug}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Link
                                        to={`/blog/${post.slug}`}
                                        className="flex flex-col sm:flex-row bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                                    >
                                        {post.coverImage && (
                                            <div className="w-full sm:w-1/3 md:w-1/4 h-48 sm:h-auto shrink-0 overflow-hidden">
                                                <img 
                                                    src={post.coverImage} 
                                                    alt={post.title} 
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                                                />
                                            </div>
                                        )}
                                        <div className="p-4 sm:p-6 flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-3 mb-3">
                                                <span
                                                className="px-3 py-1 text-sm rounded-full"
                                                style={{
                                                    backgroundColor: `${post.categoryColor}1a`,
                                                    color: post.categoryColor,
                                                }}
                                            >
                                                {post.categoryName}
                                            </span>
                                            {post.featured && (
                                                <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 text-sm rounded-full">
                                                    精选
                                                </span>
                                            )}
                                            <span className="text-gray-400 text-sm flex items-center gap-1">
                                                <Clock className="w-4 h-4" />
                                                {post.readingTime} 分钟
                                            </span>
                                            <span className="text-gray-400 text-sm flex items-center gap-1">
                                                <Eye className="w-4 h-4" />
                                                {post.views}
                                            </span>
                                        </div>

                                        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                            {post.title}
                                        </h3>

                                        <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">{post.excerpt}</p>

                                        <div className="flex flex-wrap items-center justify-between gap-4">
                                            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                                <div className="flex items-center gap-1">
                                                    <User className="w-4 h-4" />
                                                    {post.author}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    {formatDate(post.date)}
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap gap-2 justify-end mt-auto pt-2">
                                                {post.tags.slice(0, 3).map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>

                        {filteredPosts.length === 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl p-10 text-center text-gray-500 dark:text-gray-400">
                                没有找到匹配文章，试试更换关键词或分类。
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-8">
                                <button
                                    onClick={() => setCurrentPage((value) => Math.max(1, value - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 bg-white dark:bg-gray-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-4 py-2 rounded-lg ${
                                            currentPage === page
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage((value) => Math.min(totalPages, value + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 bg-white dark:bg-gray-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="hidden lg:block space-y-4 sm:space-y-6 xl:sticky xl:top-24 self-start">
                        {profile && (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm"
                            >
                                <h3 className="font-bold text-gray-800 dark:text-white mb-4">作者资料</h3>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold flex items-center justify-center">
                                        {profile.name?.charAt(0) || 'L'}
                                    </div>
                                    <div>
                                        <div className="font-medium text-gray-800 dark:text-white">{profile.name}</div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">{profile.title}</div>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 leading-6">{profile.bio}</p>
                                {profile.location && (
                                    <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        {profile.location}
                                    </div>
                                )}
                            </motion.div>
                        )}

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm"
                        >
                            <div className="flex items-center gap-2 mb-3 sm:mb-4">
                                <Filter className="w-5 h-5 text-blue-500" />
                                <h3 className="font-bold text-gray-800 dark:text-white">分类</h3>
                            </div>
                            <div className="space-y-1 sm:space-y-2">
                                <button
                                    onClick={() => handleCategoryChange('')}
                                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                        !selectedCategory
                                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                                            : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    全部文章 ({posts.length})
                                </button>

                                {categories.map((category) => (
                                    <button
                                        key={category.slug}
                                        onClick={() => handleCategoryChange(category.slug)}
                                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                            selectedCategory === category.slug
                                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                                                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        {category.name} ({category.postCount})
                                    </button>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.15 }}
                            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm"
                        >
                            <div className="flex items-center gap-2 mb-3 sm:mb-4">
                                <Tag className="w-5 h-5 text-green-500" />
                                <h3 className="font-bold text-gray-800 dark:text-white">标签云</h3>
                            </div>
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                {tags.slice(0, 18).map((tag) => (
                                    <button
                                        key={tag.slug}
                                        onClick={() => {
                                            setSearchQuery(tag.name);
                                            setCurrentPage(1);
                                        }}
                                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-full hover:bg-blue-100 dark:hover:bg-blue-900 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                        style={{ fontSize: `${0.78 + Math.min(tag.count, 6) * 0.05}rem` }}
                                    >
                                        {tag.name} ({tag.count})
                                    </button>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                            className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 sm:p-6 text-white"
                        >
                            <h3 className="font-bold mb-4 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5" />
                                博客统计
                            </h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>文章总数</span>
                                    <span className="font-bold">{stats?.publishedPosts ?? posts.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>总访问量</span>
                                    <span className="font-bold">{stats?.totalViews ?? 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>今日访问</span>
                                    <span className="font-bold">{stats?.viewsToday ?? 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>分类数量</span>
                                    <span className="font-bold">{stats?.categories ?? categories.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>标签数量</span>
                                    <span className="font-bold">{stats?.tags ?? tags.length}</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogList;
